/**
 * The Home hero's background: a looping fight made with Higgsfield (Spike and Moss in the snow
 * octagon, docs/architecture.md §12 "Home hero video"). Wide for desktop and phones on their side,
 * tall for phones upright. Layered so it never shows half an image: a tiny blurred copy of the
 * first frame at once (as in the loading skeleton, index.html), the first frame itself once it is
 * loaded and decoded, then the video once it plays. The first frame stays alone with reduced
 * motion or Data Saver, and wherever the video doesn't start on its own.
 */
import { useEffect, useRef, useState } from 'react';
import tallVideo from '../../assets/hero/home-tall.mp4';
import tallPlaceholder from '../../assets/hero/home-tall-placeholder.webp?inline';
import tallPoster from '../../assets/hero/home-tall.webp';
import wideVideo from '../../assets/hero/home-wide.mp4';
import widePlaceholder from '../../assets/hero/home-wide-placeholder.webp?inline';
import widePoster from '../../assets/hero/home-wide.webp';
import { heroShown } from '../../platform/firstScreen';
import { useReducedMotion } from '../hooks';
import s from './HeroVideo.module.css';
import ui from './ui.module.css';

const VIDEOS = {
  wide: { video: wideVideo, poster: widePoster, placeholder: widePlaceholder },
  tall: { video: tallVideo, poster: tallPoster, placeholder: tallPlaceholder },
} as const;

export type HeroShape = keyof typeof VIDEOS;

/** First frames already shown in this visit: back on Home, they're there at once. */
const shownPosters = new Set<string>();

function saveData(): boolean {
  return !!(navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;
}

interface HeroVideoProps {
  shape: HeroShape;
  /** A CSS background on top (gradients that keep the title and buttons readable). */
  shade?: string;
  /** Show the video's whole width, faded out at the top and bottom, instead of covering the box. */
  fitWidth?: boolean;
  /** Flipped left to right, to keep the fighters away from the buttons. */
  mirror?: boolean;
}

/** Absolutely positioned (the parent must be positioned), with a shade on top like ArenaBackdrop. */
export function HeroVideo(props: HeroVideoProps) {
  // Turning a phone changes the shape: the other video starts again from its placeholder.
  return <HeroMedia key={props.shape} {...props} />;
}

function HeroMedia({ shape, shade, fitWidth = false, mirror = false }: HeroVideoProps) {
  const reduced = useReducedMotion();
  const still = reduced || saveData();
  const ref = useRef<HTMLVideoElement>(null);
  const v = VIDEOS[shape];
  // The placeholder stays under the first frame until that has faded in over it.
  const [poster, setPoster] = useState<'loading' | 'fading' | 'shown' | 'failed'>(() =>
    shownPosters.has(v.poster) ? 'shown' : 'loading',
  );
  const [playing, setPlaying] = useState(false);

  // iOS only autoplays a muted video, and React sets `muted` as a property after the element is
  // made; start it here too (a refused play leaves the first frame).
  const hasVideo = !still && poster !== 'loading';
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.muted = true;
    el.play().catch(() => {});
  }, [hasVideo]);

  const cls = [ui.backdropImg, s.media, fitWidth ? s.fitWidth : ''].join(' ');
  const flip = mirror ? { scale: '-1 1' } : undefined;
  return (
    <div className={ui.backdrop} aria-hidden="true">
      {poster !== 'shown' && (
        <img
          className={[cls, s.placeholder].join(' ')}
          src={v.placeholder}
          alt=""
          style={flip}
          draggable={false}
          decoding="sync"
        />
      )}
      {/* Shown once decoded, never painted in strips as it arrives. */}
      <img
        className={[cls, s.poster, poster === 'fading' || poster === 'shown' ? s.on : ''].join(' ')}
        src={v.poster}
        alt=""
        style={flip}
        draggable={false}
        onLoad={(e) => {
          if (poster !== 'loading') return;
          const show = () => {
            shownPosters.add(v.poster);
            setPoster('fading');
            heroShown();
          };
          e.currentTarget.decode().then(show, show);
        }}
        onTransitionEnd={() => setPoster((p) => (p === 'fading' ? 'shown' : p))}
        onError={() => {
          setPoster('failed');
          heroShown();
        }}
      />
      {/* Loaded after the first frame, so the much bigger file doesn't hold it up. Its first frame
          is the poster, so it simply takes over once it plays. */}
      {hasVideo && (
        <video
          ref={ref}
          className={[cls, playing ? '' : s.waiting].join(' ')}
          style={flip}
          src={v.video}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          disablePictureInPicture
          onPlaying={() => setPlaying(true)}
        />
      )}
      {shade && <div className={ui.backdropShade} style={{ background: shade }} />}
    </div>
  );
}
